import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Eye, Calendar, MapPin, Clock, ArrowRight, ChevronDown, ChevronUp, Utensils } from "lucide-react";

const roomImages = [
  { src: "https://assets.auxodev.dk/assets/4b804bad-4524-4807-89e7-374d3df315fb/8f57a5e8-42a1-4830-813d-a04801408ed2.jpeg", alt: "Smuk bordopdækning i Stuen" },
  { src: "https://assets.auxodev.dk/assets/4b804bad-4524-4807-89e7-374d3df315fb/d111da3b-70ff-456c-8a46-458a77f7485c.jpeg", alt: "Bordopdækning i Plenum" },
  { src: "https://assets.auxodev.dk/assets/4b804bad-4524-4807-89e7-374d3df315fb/52acfc56-f6ce-44a4-9043-e6bec44ef216.jpeg", alt: "Stuen klar til gæster" },
  { src: "https://assets.auxodev.dk/assets/4b804bad-4524-4807-89e7-374d3df315fb/ad610217-c843-4f94-84ea-3a8213ae465d.jpeg", alt: "Selskabslokale på 1. sal" },
  { src: "https://assets.auxodev.dk/assets/4b804bad-4524-4807-89e7-374d3df315fb/16249e7e-3a4e-4fa5-adbd-6f8eaef65e4c.jpeg", alt: "Plenum selskabslokale" },
  { src: "https://assets.auxodev.dk/assets/4b804bad-4524-4807-89e7-374d3df315fb/fa0c0eb7-6ce6-4ffb-a30d-6a368b2077a6.jpeg", alt: "Folkestuen klar til fest" }
];

const selskabBoxes = [
  {
    id: "konfirmation",
    title: "Konfirmation",
    desc: "Menuer og rammer til en hyggelig konfirmationsdag.",
    actionText: "Se menukort",
    targetId: "selskabsmenuer",
  },
  {
    id: "bryllup",
    title: "Bryllup",
    desc: "Selskabsmenuer til bryllup og festlige anledninger.",
    actionText: "Se menukort",
    targetId: "selskabsmenuer",
  },
  {
    id: "barnedaab",
    title: "Barnedåb",
    desc: "Rolige og indbydende rammer til barnedåb.",
    actionText: "Se menukort",
    targetId: "selskabsmenuer",
  },
  {
    id: "bisaettelse",
    title: "Bisættelse",
    desc: "En værdig og rolig ramme om samvær efter bisættelsen.",
    actionText: "Se menukort",
    targetId: "selskabsmenuer",
  },
  {
    id: "julefrokost",
    title: "Julefrokost",
    desc: "Menuer og selskabsmuligheder til årets julefrokoster.",
    actionText: "Se menukort",
    targetId: "selskabsmenuer",
  },
  {
    id: "nytaarsmenu",
    title: "Nytårsmenu",
    desc: "Festlige menuer og muligheder til nytår.",
    actionText: "Se menukort",
    targetId: "selskabsmenuer",
  },
];

const selskabMenus = {
  sharefood: {
    title: "1. Sharefood menu",
    price: "595,- pr. kuvert",
    sections: [
      {
        title: "1. servering",
        items: [
          "Rimet laks med ponzu og avocado samt ristede sesam",
          "Kroketter af kartoffel med chorizo creme",
          "Små grøntsagstærter med kartoffel og rosmarin samt syrlig krydderurtesalat",
          "Focaccia brød med smør"
        ]
      },
      {
        title: "2. servering",
        items: [
          "Helstegt citronkylling",
          "Okseculotte med fermenteret peber",
          "Ristede kartofler vendt i persille",
          "Glaserede sæsongrøntsager med nøddecrumble"
        ]
      },
      {
        title: "Dessert",
        items: [
          "Trifli med kompot af sæsonfrugt samt flødeskum"
        ]
      }
    ]
  },
  treRetters: {
    title: "2. 3-retters menu",
    price: "595,- pr. kuvert",
    sections: [
      {
        title: "Forret",
        items: [
          "Fisk en crouton med rejesoufflé, fyldig muslingesauce mættet med småtskårne rodfrugter og krydderurter",
          "Brød og smør"
        ]
      },
      {
        title: "Hovedret",
        items: [
          "Helstegt kalvefilet med glaserede gulerødder, puré af karamelliseret løg og sauce demi-glace",
          "Ristede kartofler serveret separat"
        ]
      },
      {
        title: "Dessert",
        items: [
          "Chokoladekage med hyldeblomst/hvid chokolade-mousse samt vaniljecreme"
        ]
      }
    ]
  },
  brunch: {
    title: "3. Brunch buffet",
    price: "425,- pr. kuvert",
    items: [
      "Røræg med purløg",
      "Stegt bacon",
      "Stegte brunchpølser",
      "Charcuterie med serrano, chorizo og salami samt garniture",
      "Tomatsalat med syltede rødløg og sød sennepsvinaigrette",
      "Cæsarsalat med parmesandressing og sprøde croutoner",
      "Frugtopsætning af hel og skåret frugt",
      "Oste med frugtkompot",
      "Pandekager med sirup",
      "Juice, isvand, te og kaffe ad libitum",
      "Hjemmebagt lyst brød",
      "Hjemmebagt mørkt brød"
    ],
    tilkoeb: "Duge og blomster: +75,- pr. kuvert",
    tilvalg: [
      { name: "Mimosa til velkomst", price: "40,-" },
      { name: "Bagt laks med sesam", price: "+40,-" },
      { name: "Koldrøget laks med purløg", price: "+40,-" },
      { name: "Mini croissanter", price: "+15,-" },
      { name: "Kanelsnegle", price: "+20,-" }
    ]
  },
  frokost: {
    title: "4. Frokost buffet",
    price: "565,- pr. kuvert",
    items: [
      "Serrano i skiver serveret med oliven",
      "Bagt laks med krydderurtedressing",
      "Friterede papadums serveret med hummus og olivenolie",
      "Små kartoffeltærter med krydderurtesalat og cremefraiche",
      "Dry rub-udbenet kylling glaseret i østerssauce og sesam",
      "Asparges med ærtecreme og citronolie",
      "Spidskålsalat med røde æbler, persille og saltede græskarkerner",
      "Cæsarsalat med syltede løg, croutoner og parmesancreme",
      "Focacciabrød med smør",
      "Krustader med chokolademousse",
      "Krustader med lemoncurd og marengs"
    ]
  },
  adLibitum: {
    title: "5. Ad libitum-pakker",
    price: "Se priser herunder",
    items: [
      { name: "Husets vin, øl og sodavand i 5 timer", price: "500,-" },
      { name: "Husets vin, øl og sodavand i 7 timer", price: "650,-" },
      { name: "Husets vin, øl, sodavand og bar-sjusser i 9 timer", price: "800,-" },
      { name: "Drinks som tilkøb til eksisterende pakke", price: "50,- pr. gæst" },
      { name: "Drinks efter forbrug", price: "80,- pr. stk." }
    ]
  }
};

// Helper to get Cloudflare resized image URL
const getResizedUrl = (url, width = 600) => {
  if (!url.startsWith("https://assets.auxodev.dk/")) return url;
  return url.replace("https://assets.auxodev.dk/", `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=80,format=auto/`);
};

export default function SelskaberEvents() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [openAccordion, setOpenAccordion] = useState("sharefood");

  const toggleAccordion = (key) => {
    setOpenAccordion(openAccordion === key ? null : key);
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(roomImages[index]);
  };

  const nextImage = (e) => {
    e?.stopPropagation();
    const newIndex = (currentIndex + 1) % roomImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(roomImages[newIndex]);
  };

  const prevImage = (e) => {
    e?.stopPropagation();
    const newIndex = (currentIndex - 1 + roomImages.length) % roomImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(roomImages[newIndex]);
  };

  return (
    <div className="bg-white min-h-screen pt-20 font-sans">
      
      {/* Hero Block */}
      <section 
        className="relative min-h-[450px] md:min-h-[55vh] flex items-center justify-center bg-cover bg-center py-20"
        style={{ backgroundImage: 'url("https://assets.auxodev.dk/assets/4b804bad-4524-4807-89e7-374d3df315fb/8f57a5e8-42a1-4830-813d-a04801408ed2.jpeg")' }}
      >
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
             <h1 className="text-4xl md:text-6xl text-white font-serif tracking-wide text-shadow-sm mb-6">Selskaber & Events</h1>
             <p className="text-white/90 text-lg md:text-xl leading-relaxed font-light">
               Hold jeres næste fest eller arrangement i vores smukke, lyse lokaler. Vi skaber den perfekte ramme om livets store begivenheder.
             </p>
          </motion.div>
        </div>
      </section>

      {/* Content Block */}
      <section className="py-16 md:py-24 bg-white text-gray-800">
         <div className="container mx-auto px-6 max-w-7xl">
            
            <div className="max-w-4xl mx-auto space-y-6 text-left text-gray-700 leading-relaxed">
               <span className="text-primary tracking-widest text-xs uppercase font-semibold italic">Unikke Rammer</span>
               <h2 className="text-3xl md:text-4xl font-serif text-gray-900">
                 Selskaber og festlige begivenheder i Spisehuset
               </h2>
               
               <p className="text-lg font-light text-gray-600">
                 Spisehuset i Værløse danner de perfekte rammer om jeres næste selskab. Beliggende i naturskønne omgivelser, kun en kort køretur (ca. 25 minutter) fra Københavns pulserende hjerte, tilbyder vi et fredfyldt fristed, hvor I kan fejre livets store øjeblikke. Her forenes den smukke natur med en hjertevarm atmosfære og kompromisløs madlavning.
               </p>
               
               <h3 className="text-2xl font-serif text-gray-900 pt-4">De smukke omgivelser og den helt særlige stemning</h3>
               <p>
                 Når I træder ind i Spisehuset, mærker I straks den hyggelige og uformelle stemning, som huset emmer af. Vores lokaler er lyse og indbydende med store vinduespartier, der trækker den omkringliggende natur helt ind til bords. Uanset om I planlægger et romantisk bryllup, en rund fødselsdag, konfirmation, reception eller et firmaarrangement, tilpasser vi rammerne, så de matcher jeres drømme. Den rolige beliggenhed giver jeres gæster plads til at slappe af og nyde dagen i fulde drag, væk fra storbyens stress og jag.
               </p>
               
               <p>
                 Vi råder over flotte, fleksible lokaler med mulighed for selskaber fra 15 til 95 personer. Vores lokaler på 1. sal rummer 15-50 gæster, mens stuen har plads til 15-80 gæster. Vi skræddersyr bordopstillingen og pyntningen, så det passer præcis til jeres fest.
               </p>
               
               <p>
                 Vores dygtige køkkenpersonale sammensætter velsmagende selskabsmenuer baseret på sæsonens bedste råvarer. Vi tager naturligvis højde for eventuelle allergener og særlige kostbehov, så alle gæster får en fantastisk spiseoplevelse.
               </p>

               <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-6 border-t border-gray-100 mt-8">
                 <div className="flex items-start gap-3">
                   <div className="p-2 bg-primary/10 text-primary rounded">
                     <MapPin size={20} />
                   </div>
                   <div>
                     <h4 className="font-semibold text-gray-900 text-sm">Beliggenhed</h4>
                     <p className="text-xs text-gray-500 mt-1">Kun 25 min. fra København i smuk natur.</p>
                   </div>
                 </div>
                 <div className="flex items-start gap-3">
                   <div className="p-2 bg-primary/10 text-primary rounded">
                     <Clock size={20} />
                   </div>
                   <div>
                     <h4 className="font-semibold text-gray-900 text-sm">Kapacitet</h4>
                     <p className="text-xs text-gray-500 mt-1">Selskaber fra 15 til 95 personer.</p>
                   </div>
                 </div>
                 <div className="flex items-start gap-3">
                   <div className="p-2 bg-primary/10 text-primary rounded">
                     <Calendar size={20} />
                   </div>
                   <div>
                     <h4 className="font-semibold text-gray-900 text-sm">Skræddersyet</h4>
                     <p className="text-xs text-gray-500 mt-1">Vi tilpasser alt efter jeres ønsker.</p>
                   </div>
                 </div>
               </div>
            </div>

         </div>
      </section>

      {/* Selskabs- & Anledningsbokse Section */}
      <section className="py-16 md:py-24 bg-stone-50 border-t border-gray-100">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center mb-12">
            <span className="text-primary tracking-widest text-xs uppercase font-semibold italic">Anledninger</span>
            <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mt-2">Fejr livets store øjeblikke hos os</h2>
            <p className="text-gray-500 mt-4 max-w-xl mx-auto font-light">
              Vi tilbyder de perfekte rammer og skræddersyede løsninger til enhver begivenhed.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {selskabBoxes.map((box) => (
              <div 
                key={box.id}
                className="bg-white p-8 border border-stone-200/60 rounded-sm shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between group"
              >
                <div>
                  <h3 className="font-serif text-2xl text-stone-900 mb-3 group-hover:text-primary transition-colors">{box.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6 font-light">{box.desc}</p>
                </div>
                <div>
                  {box.link ? (
                    <Link 
                      to={box.link}
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-primary hover:text-dark transition-colors"
                    >
                      {box.actionText} <ArrowRight size={14} />
                    </Link>
                  ) : (
                    <button 
                      onClick={() => scrollToSection(box.targetId)}
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-primary hover:text-dark transition-colors cursor-pointer"
                    >
                      {box.actionText} <ArrowRight size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Menu Examples Section */}
      <section id="selskabsmenuer" className="py-16 md:py-24 bg-white border-y border-gray-100 text-gray-800">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-12">
            <span className="text-primary tracking-widest text-xs uppercase font-semibold italic">Gastronomi</span>
            <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mt-2">Menukort til selskaber</h2>
            <p className="text-gray-500 mt-4 max-w-xl mx-auto font-light">
              Herunder kan du se vores udvalg af selskabsmenuer og buffeter. Klik på en menu for at se retterne.
            </p>
          </div>

          {/* Accordion Wrapper */}
          <div className="space-y-4">
            {Object.entries(selskabMenus).map(([key, menu]) => {
              const isOpen = openAccordion === key;
              return (
                <div 
                  key={key} 
                  className="border border-stone-200 rounded-sm overflow-hidden transition-all duration-300 shadow-sm"
                >
                  {/* Accordion Header */}
                  <button
                    onClick={() => toggleAccordion(key)}
                    className={`w-full flex items-center justify-between p-6 text-left transition-colors cursor-pointer ${
                      isOpen ? "bg-stone-50" : "bg-white hover:bg-stone-50/50"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-primary/10 flex items-center justify-center rounded-sm shrink-0">
                        <Utensils className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-serif text-xl text-stone-900 font-medium">{menu.title}</h3>
                      </div>
                    </div>
                    <div className="text-stone-400">
                      {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                  </button>

                  {/* Accordion Content */}
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden bg-white border-t border-stone-100"
                      >
                        <div className="p-8 space-y-8">
                          {/* Render different menus based on structure */}
                          {menu.sections ? (
                            // Sharefood or 3-retters menu
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                              {menu.sections.map((section, sIdx) => (
                                <div key={sIdx} className="space-y-4">
                                  <h4 className="font-serif text-lg text-primary border-b border-stone-100 pb-2 font-medium">
                                    {section.title}
                                  </h4>
                                  <ul className="space-y-3 text-sm text-stone-600 font-light leading-relaxed">
                                    {section.items.map((item, iIdx) => (
                                      <li key={iIdx} className="flex items-start gap-2">
                                        <span className="text-primary mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-primary" />
                                        <span>{item}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          ) : (
                            // Buffet or Ad libitum
                            <div className="space-y-6">
                              {menu.items && Array.isArray(menu.items) && typeof menu.items[0] === "string" ? (
                                // Plain list (Brunch / Frokost buffet)
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-3">
                                  {menu.items.map((item, iIdx) => (
                                    <div key={iIdx} className="flex items-start gap-2 text-sm text-stone-600 font-light leading-relaxed">
                                      <span className="text-primary mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-primary" />
                                      <span>{item}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                // Object list (Ad libitum packages)
                                <div className="space-y-4 max-w-2xl mx-auto">
                                  {menu.items.map((item, iIdx) => (
                                    <div key={iIdx} className="flex justify-between items-center border-b border-stone-100 pb-3 text-sm text-stone-600">
                                      <span className="font-medium text-stone-800">{item.name}</span>
                                      <span className="text-primary font-serif font-medium">{item.price}</span>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Brunch Tilkøb & Tilvalg */}
                              {menu.tilvalg && (
                                <div className="mt-8 pt-8 border-t border-stone-100 grid grid-cols-1 md:grid-cols-2 gap-8">
                                  <div>
                                    <h4 className="font-serif text-base text-stone-900 font-medium mb-3">Tilkøb</h4>
                                    <p className="text-sm text-stone-600 italic">{menu.tilkoeb}</p>
                                  </div>
                                  <div>
                                    <h4 className="font-serif text-base text-stone-900 font-medium mb-3">Tilvalg pr. person</h4>
                                    <div className="space-y-2">
                                      {menu.tilvalg.map((item, tIdx) => (
                                        <div key={tIdx} className="flex justify-between text-xs text-stone-600">
                                          <span>{item.name}</span>
                                          <span className="font-medium text-stone-800">{item.price}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Price at the bottom */}
                          <div className="pt-6 border-t border-stone-100 flex flex-col sm:flex-row justify-between items-center gap-4">
                            <div className="text-xs text-stone-400 italic">
                              * Alle priser er angivet i DKK pr. person.
                            </div>
                            <div className="text-right">
                              <span className="text-xs uppercase tracking-wider text-stone-400 block">Pris pr. kuvert</span>
                              <span className="text-2xl font-serif text-primary font-semibold">{menu.price}</span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Opdækkede Lokaler Gallery Block */}
      <section className="py-16 md:py-24 bg-white">
         <div className="container mx-auto px-6 max-w-7xl">
            <h2 className="text-3xl font-serif text-center mb-2">Vores opdækkede lokaler</h2>
            <p className="text-gray-500 text-center mb-12 max-w-xl mx-auto font-light">
              Se billeder af vores smukke, lyse lokaler dækket op og klar til festlige begivenheder.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {roomImages.map((img, index) => (
                <motion.div 
                  key={index} 
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="group relative overflow-hidden h-[200px] md:h-[300px] cursor-zoom-in rounded shadow-sm"
                  onClick={() => openLightbox(index)}
                >
                  <img 
                    src={getResizedUrl(img.src, 600)} 
                    alt={img.alt} 
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 bg-white text-gray-900 px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-opacity duration-300 flex items-center gap-2 rounded shadow">
                      <Eye size={14} /> Se billede
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Contact Section */}
            <div className="mt-20 bg-bg-light p-8 md:p-12 rounded border border-gray-100 text-center max-w-3xl mx-auto">
              <h3 className="text-2xl font-serif text-gray-900 mb-4">Lad os planlægge jeres næste fest</h3>
              <p className="text-gray-600 mb-8 leading-relaxed font-light">
                Vi viser meget gerne vores lokaler frem og tager en uforpligtende snak om jeres ønsker til menu, bordopstilling og pyntning.
              </p>
              <div className="flex flex-wrap justify-center gap-6 text-sm">
                <p className="flex flex-col items-center">
                  <span className="text-[10px] uppercase tracking-widest text-primary font-semibold mb-1">Ring til os</span>
                  <a href="tel:42952240" className="text-gray-900 font-medium hover:text-primary transition-colors text-lg">42 95 22 40</a>
                </p>
                <div className="w-px bg-gray-200 hidden sm:block" />
                <p className="flex flex-col items-center">
                  <span className="text-[10px] uppercase tracking-widest text-primary font-semibold mb-1">Skriv en mail</span>
                  <a href="mailto:jjfoodsolution@outlook.com" className="text-gray-900 font-medium hover:text-primary transition-colors text-lg">jjfoodsolution@outlook.com</a>
                </p>
              </div>
              <div className="mt-8">
                <Link 
                  to="/kontakt" 
                  className="inline-block bg-primary text-white px-8 py-4 hover:bg-primary/90 transition-colors uppercase tracking-widest text-xs font-semibold"
                >
                  Udfyld kontaktformular
                </Link>
              </div>
            </div>
         </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button 
              className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors z-50"
              onClick={() => setSelectedImage(null)}
            >
              <X size={32} />
            </button>

            <button 
              className="absolute left-6 text-white/70 hover:text-white transition-colors"
              onClick={prevImage}
            >
              <ChevronLeft size={48} strokeWidth={1} />
            </button>

            <motion.img 
              key={selectedImage.src}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              src={selectedImage.src} 
              alt={selectedImage.alt}
              className="max-w-full max-h-[85vh] object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />

            <button 
              className="absolute right-6 text-white/70 hover:text-white transition-colors"
              onClick={nextImage}
            >
              <ChevronRight size={48} strokeWidth={1} />
            </button>

            <div className="absolute bottom-8 left-0 right-0 text-center">
               <p className="text-white/60 text-sm tracking-widest uppercase">{selectedImage.alt}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}
